import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { memo } from 'react';

import { IconCircleCheck as CheckCircleIcon } from '@/components/icon/tabler/IconCircleCheck';
import Button from '@/components/Button';
import { green, gray, radiusTokens } from '@/theme/themePrimitives';
import { mapEmailToProvider } from '@/utils/email';
import { useTranslate } from '@/state/i18n';

interface SignUpSuccessModalProps {
  open: boolean;
  email: string;
  onGoToLogin: () => void;
}

const SignUpSuccessModal = memo(({ open, email, onGoToLogin }: SignUpSuccessModalProps) => {
  const t = useTranslate('domain.auth.components.SignUpSuccessModal');
  const providerUrl = mapEmailToProvider(email).provider?.url;

  return (
    <Modal open={open} onClose={() => {}} aria-labelledby="signup-success-title">
      <Box sx={sx.modalOverlay}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="signup-success-title"
          sx={sx.modalContainer}
          onClick={(e) => e.stopPropagation()}
        >
          <CheckCircleIcon sx={{ fontSize: 64, color: green[500], mb: 2 }} />
          <Typography
            id="signup-success-title"
            variant="h5"
            fontWeight={600}
            sx={{ textAlign: 'center', mb: 1.5 }}
          >
            {t('title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', color: gray[600], mb: 3, lineHeight: 1.6 }}
          >
            {t('sentLinkLead')}
            <Link
              href={providerUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 600, color: gray[800], cursor: 'pointer' }}
            >
              {email}
            </Link>
            {t('sentLinkTail')}
          </Typography>
          <Button variant="contained" fullWidth onClick={onGoToLogin}>
            {t('goToSignIn')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
});

SignUpSuccessModal.displayName = 'SignUpSuccessModal';

const sx = {
  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 3,
  },
  modalContainer: {
    bgcolor: 'background.paper',
    borderRadius: radiusTokens.xl,
    p: 3,
    maxWidth: 360,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: 3,
  },
};

export default SignUpSuccessModal;
